import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote, Mic, Users, BookOpen } from "lucide-react";
import { brand, homeTiles, testimonials, blogPillars } from "../mock/mock";

const iconFor = { "BOSSY Community": Users, Podcast: Mic, Blog: BookOpen };

export default function Home() {
  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative">
        <div className="max-w-5xl mx-auto px-5 pt-16 pb-10 text-center">
          <p className="reveal font-ui tracking-[0.3em] text-teal text-sm font-semibold uppercase">{brand.est}</p>
          <h1 className="reveal font-serif-display font-bold text-navy leading-[0.95] mt-4 text-6xl md:text-8xl">
            Kelly &amp; Kelly
          </h1>
          <p className="reveal-2 font-serif-body text-2xl md:text-3xl text-navy/85 mt-6 max-w-3xl mx-auto leading-snug">
            Two business owners who stopped waiting for a seat at the table and created a new one.
            <span className="text-orange"> Real tools, real talk.</span>
          </p>
        </div>

        <div className="max-w-6xl mx-auto px-5">
          <div className="reveal-3 relative rounded-2xl overflow-hidden shadow-[0_30px_60px_-30px_rgba(22,38,63,0.5)]">
            <img src={brand.heroImage} alt="BOSSY founders" className="w-full h-[340px] md:h-[520px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <div className="bg-cream/95 backdrop-blur rounded-xl p-6 md:p-8 max-w-md">
                <h2 className="font-serif-display text-2xl md:text-3xl font-semibold text-navy leading-tight">
                  Workbooks &amp; digital downloads for owners done winging it.
                </h2>
                <Link to="/shop" className="mt-5 inline-flex items-center gap-2 bg-navy text-cream px-6 py-3 rounded-full font-ui font-medium hover:bg-orange transition-colors group">
                  Shop Now <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-5xl mx-auto px-5 py-16">
        <div className="grid grid-cols-3 gap-4">
          {brand.stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-serif-display text-4xl md:text-6xl font-bold text-navy">{s.value}</div>
              <div className="font-ui text-xs md:text-sm text-navy/60 mt-2 uppercase tracking-wider">{s.label}</div>
            </div>
          ))}
        </div>
        <p className="text-center font-serif-body text-xl md:text-2xl text-navy/70 mt-10 italic">
          Educators, community leaders, panelists &amp; keynote speakers.
        </p>
      </section>

      {/* STOP FIGURING IT OUT ALONE */}
      <section className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-navy">Stop Figuring It Out Alone</h2>
            <p className="font-ui text-navy/70 mt-5 text-lg leading-relaxed">
              Get the support, stories, and straight-talk you need to grow. Dive into our community, tune into the
              podcast, and explore the blog for real-life lessons from women who've been where you are.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-7">
            {homeTiles.map((t) => {
              const Icon = iconFor[t.title] || BookOpen;
              return (
                <div key={t.title} className="lift bg-cream rounded-2xl overflow-hidden flex flex-col border" style={{ borderColor: "var(--cream-dark)" }}>
                  <div className="relative h-52 overflow-hidden">
                    <img src={t.img} alt={t.title} className="w-full h-full object-cover" />
                    <div className="absolute top-4 left-4 w-11 h-11 rounded-full bg-cream/95 flex items-center justify-center">
                      <Icon size={20} className="text-navy" />
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-serif-display text-2xl font-semibold text-navy">{t.title}</h3>
                    <p className="font-ui text-navy/70 text-sm mt-3 leading-relaxed flex-1">{t.text}</p>
                    <Link to={t.to} className="mt-5 inline-flex items-center gap-2 text-teal font-ui font-semibold hover:gap-3 transition-all">
                      {t.cta} <ArrowRight size={17} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PILLARS PREVIEW */}
      <section className="max-w-6xl mx-auto px-5 py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="font-ui tracking-[0.25em] text-teal text-sm font-semibold uppercase">What we cover</p>
          <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-navy mt-3">Five things we help you own</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {blogPillars.map((p) => (
            <Link key={p.slug} to="/blog" className="lift group rounded-xl overflow-hidden relative h-44 flex items-end">
              <img src={p.img} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${p.color}ee, ${p.color}55 55%, transparent)` }} />
              <h3 className="relative font-serif-display text-white text-lg font-semibold p-4 leading-tight">{p.title}</h3>
            </Link>
          ))}
        </div>
      </section>

      {/* COMMUNITY CTA */}
      <section className="bg-navy text-cream py-20">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <p className="font-ui tracking-[0.25em] text-teal text-sm font-semibold uppercase">Our Community</p>
          <h2 className="font-serif-display text-4xl md:text-6xl font-bold mt-4">The BOSSY Community</h2>
          <p className="font-ui text-white/80 mt-6 text-lg leading-relaxed max-w-2xl mx-auto">
            Empowering women entrepreneurs through shared experiences and professional growth. Join our dedicated
            support group for customized guidance and a nurturing environment.
          </p>
          <Link to="/community" className="mt-8 inline-flex items-center gap-2 bg-teal text-navy px-8 py-4 rounded-full font-ui font-semibold hover:bg-cream transition-colors group">
            Join the Group <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="max-w-4xl mx-auto px-5 py-24 text-center">
        <Quote size={48} className="text-teal mx-auto mb-6" />
        <p className="font-serif-body text-3xl md:text-4xl text-navy leading-snug italic">
          "{testimonials[0].quote}"
        </p>
        <p className="font-ui text-navy/60 mt-8 font-medium">— {testimonials[0].name}</p>
      </section>
    </div>
  );
}
